import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Search, Filter, X, Package, ChevronDown } from 'lucide-react'
import { api, PackageSummary, SearchParams } from '../utils/api'
import PackageCard from '../components/PackageCard'

const CAPABILITIES = [
  { value: 'code.analysis', label: 'Code Analysis' },
  { value: 'code.generation', label: 'Code Generation' },
  { value: 'data.database', label: 'Database' },
  { value: 'data.spreadsheet', label: 'Spreadsheet' },
  { value: 'integration.github', label: 'GitHub' },
  { value: 'integration.slack', label: 'Slack' },
  { value: 'integration.google', label: 'Google Workspace' },
  { value: 'web.search', label: 'Web Search' },
  { value: 'file.management', label: 'File Management' },
  { value: 'ai.image', label: 'Image Generation' },
]

const TRUST_TIERS = [
  { value: 'approved', label: 'Approved' },
  { value: 'unreviewed', label: 'Unreviewed' },
]

export default function BrowsePage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [packages, setPackages] = useState<PackageSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFilters, setShowFilters] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | undefined>()

  const query = searchParams.get('q') || ''
  const capability = searchParams.get('capability') || ''
  const trustTier = searchParams.get('trust_tier') || ''
  const tag = searchParams.get('tag') || ''

  const loadPackages = useCallback(async (cursor?: string) => {
    setLoading(true)
    setError(null)
    console.log('[BrowsePage] Loading packages with params:', { query, capability, trustTier, tag, cursor })

    try {
      const params: SearchParams = {
        q: query || undefined,
        capability: capability || undefined,
        trust_tier: trustTier || undefined,
        tag: tag || undefined,
        cursor,
        limit: 24,
      }
      const response = await api.searchPackages(params)

      if (cursor) {
        setPackages((prev) => [...prev, ...response.items])
      } else {
        setPackages(response.items)
      }
      setNextCursor(response.next_cursor)
    } catch (err) {
      console.error('[BrowsePage] Search failed:', err)
      setError('Failed to load packages. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [query, capability, trustTier, tag])

  useEffect(() => {
    loadPackages()
  }, [loadPackages])

  const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const formData = new FormData(e.currentTarget)
    const searchQuery = formData.get('search') as string
    updateFilter('q', searchQuery)
  }

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set(key, value)
    } else {
      newParams.delete(key)
    }
    setSearchParams(newParams)
  }

  const clearFilters = () => {
    setSearchParams(new URLSearchParams())
  }

  const hasFilters = query || capability || trustTier || tag

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <h1 className="text-3xl font-bold text-gray-900">Browse Skills</h1>
          <p className="mt-2 text-gray-600">
            Discover MCP skills for your AI assistant
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="mt-6 flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                name="search"
                placeholder="Search skills..."
                defaultValue={query}
                className="input pl-10 pr-4 py-3 w-full"
              />
            </div>
            <button type="submit" className="btn btn-primary px-6">
              Search
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className={`btn ${showFilters ? 'btn-primary' : 'btn-secondary'} px-4`}
            >
              <Filter className="w-5 h-5" />
            </button>
          </form>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Capability
                  </label>
                  <div className="relative">
                    <select
                      value={capability}
                      onChange={(e) => updateFilter('capability', e.target.value)}
                      className="input appearance-none pr-10"
                    >
                      <option value="">All capabilities</option>
                      {CAPABILITIES.map((cap) => (
                        <option key={cap.value} value={cap.value}>
                          {cap.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trust Tier
                  </label>
                  <div className="relative">
                    <select
                      value={trustTier}
                      onChange={(e) => updateFilter('trust_tier', e.target.value)}
                      className="input appearance-none pr-10"
                    >
                      <option value="">All trust tiers</option>
                      {TRUST_TIERS.map((tier) => (
                        <option key={tier.value} value={tier.value}>
                          {tier.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tag
                  </label>
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => updateFilter('tag', e.target.value)}
                    placeholder="e.g. productivity"
                    className="input"
                  />
                </div>
              </div>

              {hasFilters && (
                <div className="mt-4 flex items-center justify-end">
                  <button
                    onClick={clearFilters}
                    className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
                  >
                    <X className="w-4 h-4" />
                    <span>Clear filters</span>
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Active filters */}
          {hasFilters && !showFilters && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-500">Filters:</span>
              {query && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-100 text-primary-800">
                  "{query}"
                  <button
                    onClick={() => updateFilter('q', '')}
                    className="ml-2 hover:text-primary-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {capability && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                  {CAPABILITIES.find((c) => c.value === capability)?.label || capability}
                  <button
                    onClick={() => updateFilter('capability', '')}
                    className="ml-2 hover:text-gray-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {trustTier && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-gray-100 text-gray-800">
                  {TRUST_TIERS.find((t) => t.value === trustTier)?.label || trustTier}
                  <button
                    onClick={() => updateFilter('trust_tier', '')}
                    className="ml-2 hover:text-gray-600"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error ? (
          <div className="text-center py-12">
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={() => loadPackages()} className="btn btn-secondary">
              Try again
            </button>
          </div>
        ) : loading && packages.length === 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card p-6 animate-pulse">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
                <div className="h-3 bg-gray-200 rounded w-full mb-2" />
                <div className="h-3 bg-gray-200 rounded w-2/3" />
              </div>
            ))}
          </div>
        ) : packages.length === 0 ? (
          <div className="text-center py-16">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No skills found</h3>
            <p className="text-gray-500 mb-6">
              {hasFilters
                ? 'Try adjusting your filters or search query.'
                : 'No skills have been published yet.'}
            </p>
            {hasFilters && (
              <button onClick={clearFilters} className="btn btn-secondary">
                Clear filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <PackageCard key={pkg.package_id} package={pkg} />
              ))}
            </div>

            {nextCursor && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => loadPackages(nextCursor)}
                  disabled={loading}
                  className="btn btn-secondary"
                >
                  {loading ? 'Loading...' : 'Load more'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
